import { useEffect, useMemo, useState } from "react";

import { api } from "@/services/api";
import type { RateIngestCarrierPayload, RateIngestPayload } from "@/types/feed";
import {
  isRateIngestCarrierPayload,
  isRateIngestPayload,
  metaFromPolicySnapshots,
  metaFromRateIngestPayload,
  payloadHasRateIngestMeta,
  rateIngestCardIds,
  type RateIngestDisplayMeta,
} from "./rateIngestDisplay";

const cache = new Map<string, RateIngestDisplayMeta>();

/** Validity + policy copy for a rate-ingest card; fetches snapshots for older events. */
export function useRateIngestMeta(
  eventId: string | undefined,
  payload: unknown,
): RateIngestDisplayMeta | null {
  const ratePayload: RateIngestPayload | RateIngestCarrierPayload | null =
    isRateIngestPayload(payload)
      ? payload
      : isRateIngestCarrierPayload(payload)
        ? payload
        : null;

  const embedded = useMemo(
    () => (ratePayload ? metaFromRateIngestPayload(ratePayload) : null),
    [ratePayload],
  );
  const hasMeta = ratePayload ? payloadHasRateIngestMeta(ratePayload) : false;
  const cardIds = ratePayload ? rateIngestCardIds(ratePayload) : [];
  const cacheKey = eventId && cardIds.length > 0 ? `${eventId}:${cardIds.join(",")}` : null;

  const [fetched, setFetched] = useState<RateIngestDisplayMeta | null>(() =>
    cacheKey ? (cache.get(cacheKey) ?? null) : null,
  );

  useEffect(() => {
    if (!eventId || !ratePayload || hasMeta || cardIds.length === 0 || !cacheKey) {
      return;
    }
    if (cache.has(cacheKey)) {
      setFetched(cache.get(cacheKey) ?? null);
      return;
    }
    let cancelled = false;
    api
      .getFeedRateIngestPolicies(eventId)
      .then((res) => {
        if (cancelled) return;
        const meta = metaFromPolicySnapshots(res.policies);
        cache.set(cacheKey, meta);
        setFetched(meta);
      })
      .catch(() => {
        if (!cancelled) setFetched(null);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, ratePayload, hasMeta, cardIds.length, cacheKey]);

  if (!ratePayload) return null;
  if (hasMeta) return embedded;
  return fetched ?? embedded;
}
